import { useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'redux/store-hooks';
import { selectJustInTimeCalls } from 'redux/slices/callsSlice';
import { callsRequests } from 'redux/requests/calls.request';
import { useApiCall } from 'redux/utils/api-hook';
import { callsSlice } from 'redux/slices/callsSlice';
import type { ValidateActiveCallsResponseDto } from '@act/shared/data-transfer-objects';

interface UseActiveCallsHealthCheckOptions {
  /**
   * Interval in milliseconds between health checks
   * @default 60000 (1 minute)
   */
  intervalMs?: number;
  /**
   * Enable/disable the health check
   * @default true
   */
  enabled?: boolean;
}

/**
 * Hook that periodically validates whether active calls are still actually active.
 * Automatically removes calls from justInTimeCalls that have been ended on the backend.
 *
 * This provides a safety mechanism to prevent stuck call banners when WebSocket
 * events are missed or fail to deliver.
 *
 * @param options Configuration options for the health check
 *
 * @example
 * ```tsx
 * // In your app component
 * useActiveCallsHealthCheck();
 * ```
 *
 * @example
 * ```tsx
 * // With custom interval
 * useActiveCallsHealthCheck({ intervalMs: 30000 }); // Check every 30 seconds
 * ```
 */
export const useActiveCallsHealthCheck = (
  options: UseActiveCallsHealthCheckOptions = {}
) => {
  const { intervalMs = 60000, enabled = true } = options;

  const dispatch = useAppDispatch();
  const justInTimeCalls = useAppSelector(selectJustInTimeCalls);
  const [validateResult, isValidating, validateActiveCalls] = useApiCall(
    callsRequests.validateActiveCalls
  );

  // Use ref to track the interval ID
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);
  // Track the last processed result to prevent duplicate processing
  const lastProcessedResultRef = useRef<ValidateActiveCallsResponseDto | null>(
    null
  );
  // Keep a ref to the latest justInTimeCalls to avoid recreating the callback
  const justInTimeCallsRef = useRef(justInTimeCalls);

  useEffect(() => {
    justInTimeCallsRef.current = justInTimeCalls;
  }, [justInTimeCalls]);

  /**
   * Process validation results and clean up ended calls
   */
  useEffect(() => {
    // Only process if we have a new result
    if (
      validateResult &&
      validateResult !== lastProcessedResultRef.current &&
      !isValidating
    ) {
      // Remove ended calls from justInTimeCalls
      if (
        validateResult.endedCallIds &&
        validateResult.endedCallIds.length > 0
      ) {
        validateResult.endedCallIds.forEach(callId => {
          dispatch(callsSlice.actions.removeJustInTimeCall(callId));
        });
      }
      lastProcessedResultRef.current = validateResult;
    }
  }, [validateResult, isValidating, dispatch]);

  /**
   * Validates the current active calls and removes any that are no longer active
   */
  const performHealthCheck = () => {
    // Skip if already validating or no calls to check
    if (isValidating || justInTimeCallsRef.current.length === 0) {
      return;
    }

    const callIds = justInTimeCallsRef.current.map(call => call.id);

    // Note: validateActiveCalls is synchronous (from useApiCall).
    // Errors are handled internally by useApiCall and will show error toasts.
    // This is a limitation of useApiCall - we cannot silently handle errors here.
    validateActiveCalls({
      body: { callIds },
    });

    // Result will be processed in the useEffect above
  };

  // Keep a stable reference to performHealthCheck for external use
  const performHealthCheckRef = useRef(performHealthCheck);
  useEffect(() => {
    performHealthCheckRef.current = performHealthCheck;
  });

  /**
   * Set up the periodic health check
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    // Clear any existing interval
    if (intervalIdRef.current) {
      clearInterval(intervalIdRef.current);
    }

    // Perform initial check after a short delay (5 seconds)
    // This gives the app time to initialize and load active calls
    const initialTimeoutId = setTimeout(() => {
      performHealthCheckRef.current();
    }, 5000);

    // Set up recurring health checks
    intervalIdRef.current = setInterval(() => {
      performHealthCheckRef.current();
    }, intervalMs);

    // Cleanup function
    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
      clearTimeout(initialTimeoutId);
    };
  }, [enabled, intervalMs]);

  // Expose manual trigger for testing or manual refresh
  return {
    triggerHealthCheck: () => performHealthCheckRef.current(),
  };
};
